import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType, RootState } from "@store/store";
import { fetchUploadedDocuments } from "../application-preview/redux/thunk/UploadDocumentsThunk";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { useNavigate } from "react-router-dom";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import { resetApplicationSubmitted } from "../application-details/redux/applicationSubmittedSlice";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { getFileSignedURLThunk } from "@modules/stepper/redux/thunk/documentThunk";

export const UploadedDocuments: React.FC<{
  isExpanded: boolean;
  toggleExpand: () => void;
  visible?: boolean;
}> = ({ isExpanded, toggleExpand, visible = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { access_id, category_id } = useSelector(
    (state: RootReducerType) => state.amenities,
  );

  const application_category_id = useSelector(
    (state: RootReducerType) =>
      state.applicationDetails.applicationDetails?.application_category_id,
  );

  useEffect(() => {
    dispatch(
      fetchUploadedDocuments({
        access_id: String(access_id),
        amenityCategory: application_category_id || category_id,
      }),
    );
  }, [dispatch]);

  const { uploadDocuments } = useSelector(
    (state: RootState) => state.uploadedDocuments,
  );

  // Separate PDF and DRW files
  const pdfDocs =
    uploadDocuments?.files?.filter(
      (doc: any) =>
        doc.document_type === "FILE_UPLOAD" &&
        doc.media_type?.startsWith("pdf"),
    ) || [];

  const drawingDocs =
    uploadDocuments?.files?.filter(
      (doc: any) =>
        doc.document_type === "FILE_UPLOAD" &&
        doc.media_type?.startsWith("drw"),
    ) || [];

  if (!uploadDocuments) return;

  function getTruncatedFileName(fileName: string, maxLength = 15) {
    const lastDot = fileName?.lastIndexOf(".");
    if (lastDot === -1) return fileName; // No extension
    const name = fileName?.slice(0, lastDot);
    const ext = fileName?.slice(lastDot);
    const truncated =
      name?.length > maxLength ? name.slice(0, maxLength) + "..." : name;
    return truncated + ext;
  }

  // const openFile = (path?: string) => {
  //   if (path) {
  //     window.open(
  //       process.env.REACT_APP_API_IMAGE_URL + path,
  //       "_blank",
  //       "noopener,noreferrer",
  //     );
  //   } else {
  //     alert("View URL not found.");
  //   }
  // };

  const handleViewFile = (doc: any) => {
    openFileBySignedUrl({
      document_name: doc?.document_name,
      dispatch,
      getSignedUrlThunk: getFileSignedURLThunk,
    });
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Card sx={{ boxShadow: "none", border: "1px solid #E7E8E9" }}>
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pl: 2,
            borderBottom: "1px solid #E7E8E9",
            py: "5px",
          }}
        >
          <Typography
            className="preview-dropdown-header"
            sx={{ fontSize: 12 }}
            fontWeight="semi-bold"
          >
            Uploaded Documents
            {visible && (
              <IconButton
                className="ml-4"
                onClick={() => {
                  dispatch(setActiveStep(2));
                  dispatch(resetApplicationSubmitted());
                  navigate("/stepper");
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            )}
          </Typography>
          <IconButton aria-label="Collapse section" onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
        {isExpanded && (
          <CardContent>
            {/* Drawing Documents Section */}
            {drawingDocs.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ my: 1, fontWeight: "bold", color: "#333" }}
                >
                  Drawings ({drawingDocs.length})
                </Typography>
                {drawingDocs.map((doc: any, idx: number) => (
                  <Box key={`drw-${idx}`}>
                    <Paper
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom:
                          idx !== drawingDocs.length - 1
                            ? "1px solid #E0E0E0"
                            : "none",
                        borderRadius: "0",
                        padding: "10px",
                        backgroundColor: idx % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ width: "70%" }}
                      >
                        <h4
                          className="font-medium body-m"
                          style={{ textAlign: "justify" }}
                        >
                          {doc?.document_master_name}
                        </h4>
                        {(/removed/i.test(doc?.document_master_name) ||
                          /merged/i.test(doc?.document_master_name)) && (
                          <Tooltip
                            title={
                              /removed/i.test(doc?.document_master_name)
                                ? `This document has been removed. The same is retained solely for applications lodged prior to the date of this amendment.`
                                : `This document has been merged into "Safety Clearances from Chief Controller of Explosives, Petroleum and Explosives Safety Organisation (PESO)". The same is retained solely for applications lodged prior to the date of this amendment.`
                            }
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: 12,
                                  backgroundColor: "#ada0ef",
                                  color: "#fff",
                                },
                              },
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 20,
                                color: "#7B61FF",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <h6
                          className="font-medium underline cursor-pointer body-xs file-link"
                          onClick={() => handleViewFile(doc)}
                          title={doc.file_name}
                        >
                          {getTruncatedFileName(doc.file_name)}
                        </h6>
                        <IconButton
                          sx={{
                            background: "#FFFFFF",
                            borderRadius: "8px",
                            border: "1px solid #D0D5DD",
                            padding: "6px 8px",
                            "&:hover": { backgroundColor: "#E7E4F8" },
                          }}
                          onClick={() => handleViewFile(doc)}
                        >
                          <RemoveRedEyeOutlinedIcon className="download-icon" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}
            {/* PDF Documents Section */}
            {pdfDocs.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ my: 1, fontWeight: "bold", color: "#333" }}
                >
                  Documents ({pdfDocs.length})
                </Typography>
                {pdfDocs.map((doc: any, idx: number) => (
                  <Box key={`pdf-${idx}`}>
                    <Paper
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom:
                          idx !== pdfDocs.length - 1
                            ? "1px solid #E0E0E0"
                            : "none",
                        borderRadius: "0",
                        padding: "10px",
                        backgroundColor: idx % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ width: "70%" }}
                      >
                        <h4
                          className="font-medium body-m"
                          style={{ textAlign: "justify" }}
                        >
                          {doc?.document_master_name || "-"}
                        </h4>
                        {doc?.document_master_name &&
                          (/removed/i.test(doc?.document_master_name) ||
                            /merged/i.test(doc?.document_master_name)) && (
                            <Tooltip
                              title={
                                /removed/i.test(doc?.document_master_name)
                                  ? `This document has been removed. The same is retained solely for applications lodged prior to the date of this amendment.`
                                  : `This document has been merged into "Safety Clearances from Chief Controller of Explosives, Petroleum and Explosives Safety Organisation (PESO)". The same is retained solely for applications lodged prior to the date of this amendment.`
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: 12,
                                    backgroundColor: "#ada0ef",
                                    color: "#fff",
                                  },
                                },
                              }}
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 20,
                                  color: "#7B61FF",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <h6
                          className="font-medium underline cursor-pointer body-xs file-link"
                          onClick={() => handleViewFile(doc)}
                          title={doc.file_name}
                        >
                          {getTruncatedFileName(doc.file_name)}
                        </h6>
                        <IconButton
                          sx={{
                            background: "#FFFFFF",
                            borderRadius: "8px",
                            border: "1px solid #D0D5DD",
                            padding: "6px 8px",
                            "&:hover": { backgroundColor: "#E7E4F8" },
                          }}
                          onClick={() => handleViewFile(doc)}
                        >
                          <RemoveRedEyeOutlinedIcon className="download-icon" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}

            {/* Show message if no documents found */}
            {pdfDocs.length === 0 && drawingDocs.length === 0 && (
              <Typography sx={{ textAlign: "center", color: "#666", py: 3 }}>
                No documents uploaded yet.
              </Typography>
            )}
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
